import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseQueryWithLogoutGuard } from "../../common/createBaseQueryWithLogoutGuard";
import store from "../../../app/store";
import { calculateAPValue } from "../../../utils/Utils";
import { setSelectedBankMetricData } from "../infiniteBankPerformanceMetricsSlice/infiniteBankPerformanceMetricsSlice";
import { HttpStatusCode } from "axios";
import { setEwiNotificationStatus } from "./downloadFHSReportSlice";

export const _authToken = () => {
  const state = store.getState();
  return `${state?.authReducer?.userData?.tokenType?.toLowerCase()} ${
    state?.authReducer?.userData?.token
  }`;
};
export const _orgClientId = () => {
  const state = store.getState();
  return state?.orgReducer?.selectedFI?.orgClientId
    ? state?.orgReducer?.selectedFI?.orgClientId
    : state?.authReducer?.userData?.client_Id;
};
export const _idrssd = () => {
  const state = store.getState();
  return state?.orgReducer?.selectedFI?.idrssd;
};

const buildCustomErrorResponse = (error, status = "CUSTOM_ERROR") => ({
  error: {
    status,
    error,
  },
});

const getSettledRequestError = (result, requestName) => {
  if (result?.status === "rejected") {
    return {
      status: "CUSTOM_ERROR",
      error: result?.reason || `${requestName} request failed`,
    };
  }

  if (result?.value?.error) {
    return {
      status: result?.value?.error?.status || "CUSTOM_ERROR",
      error: result?.value?.error,
    };
  }

  if (result?.value?.data?.statusCode !== HttpStatusCode.Ok) {
    return {
      status: result?.value?.data?.statusCode || "CUSTOM_ERROR",
      error: result?.value?.data?.message || `${requestName} request failed`,
    };
  }

  return null;
};

// ---------------------------------------------------------
// RTK Query API
// ---------------------------------------------------------
export const downloadFHSReportRTK = createApi({
  reducerPath: "downloadFHSReportRTK",
  baseQuery: createBaseQueryWithLogoutGuard({
    baseUrl: process.env.REACT_APP_API_URL,
  }),
  tagTypes: [
    "DownloadReportQuarterList",
    "FHSDataBasedOnQuarter",
    "EWIDataForCurrentQuarter",
    "UpdateEWINotification",
  ],
  endpoints: (builder) => ({
    // ----------------------------------------
    // GET QUARTER LIST
    // ----------------------------------------
    getDownloadReportQuarterList: builder.query({
      query: () => {
        const isFHSRoute =
          typeof window !== "undefined" &&
          (window.location.pathname.includes("/financial-health-score") ||
            window.location.pathname.includes("/cohort-management"));

        if (!isFHSRoute) {
          return {
            data: { data: [] },
          };
        }

        return {
          url: `/bank/${_idrssd()}/fhs/quarter`,
          method: "GET",
          headers: {
            Authorization: _authToken(),
            "Content-Type": "application/json",
          },
        };
      },
      transformResponse: (response) => {
        let dropdownItems = response?.data?.map((item, index) => ({
          label: item,
          code: index === 0 ? "current" : item,
        }));
        return { data: [...dropdownItems] };
      },
      providesTags: ["DownloadReportQuarterList"],
    }),

    // ----------------------------------------
    // GET FHS DATA BASED ON QUARTER
    // ----------------------------------------
    getFHSDataBasedOnQuarter: builder.query({
      async queryFn({ quarter }, _queryApi, _extraOptions, fetchWithBQ) {
        const isFHSRoute =
          typeof window !== "undefined" &&
          (window.location.pathname.includes("/financial-health-score") ||
            window.location.pathname.includes("/cohort-management"));

        if (!isFHSRoute) {
          return {
            data: {
              cohortList: [],
              adjusted_metrics: null,
              early_warning_indicators: [],
              ewi_notification_status: null,
            },
          };
        }

        const headers = {
          Authorization: _authToken(),
          "Content-Type": "application/json",
        };
        try {
          const [fhsResult, ewiResult, cohortResult] = await Promise.allSettled(
            [
              fetchWithBQ({
                url: `bank/${_orgClientId()}/fhs/${_idrssd()}?quarter=${quarter}`,
                method: "GET",
                headers,
              }),
              fetchWithBQ({
                url: `bank/${_orgClientId()}/ewi/${_idrssd()}?quarter=${quarter}`,
                method: "GET",
                headers,
              }),
              fetchWithBQ({
                url: `/bank/${_idrssd()}/cohort?PageNumber=1&PageSize=10&isdashboard=true&quarter=${quarter}`,
                method: "GET",
                headers,
              }),
            ],
          );

          const requestError =
            getSettledRequestError(fhsResult, "FHS") ||
            getSettledRequestError(ewiResult, "EWI") ||
            getSettledRequestError(cohortResult, "Cohort");

          if (requestError) {
            return buildCustomErrorResponse(
              requestError.error,
              requestError.status,
            );
          }

          const fhsData = fhsResult?.value?.data?.data;
          const ewiData = ewiResult?.value?.data?.data;
          const cohortData = cohortResult?.value?.data?.data;

          const finalData = {
            ...fhsData,
            cohortList: cohortData ?? null,

            adjusted_metrics: ewiData?.adjusted_metrics,
            early_warning_indicators: ewiData?.early_warning_indicators,
            ewi_notification_status: ewiData?.ewi_notification_status,

            profitability: calculateAPValue(
              fhsData?.profitability,
              ewiData?.adjusted_metrics,
            ),

            growth_metrics: calculateAPValue(
              fhsData?.growth_metrics,
              ewiData?.adjusted_metrics,
            ),

            additional_regulatory_and_risk_metrics: calculateAPValue(
              fhsData?.additional_regulatory_and_risk_metrics,
              ewiData?.adjusted_metrics,
            ),
          };

          _queryApi.dispatch(setSelectedBankMetricData(finalData));
          _queryApi.dispatch(
            setEwiNotificationStatus(ewiData?.ewi_notification_status),
          );

          return {
            data: finalData,
          };
        } catch (error) {
          return buildCustomErrorResponse(error);
        }
      },

      providesTags: ["FHSDataBasedOnQuarter"],
    }),

    // ----------------------------------------
    // GET EWI DATA BASED ON QUARTER FOR ANALYST LOGIN
    // ----------------------------------------
    getEWIData: builder.query({
      async queryFn({ quarter }, _queryApi, _extraOptions, fetchWithBQ) {
        const headers = {
          Authorization: _authToken(),
          "Content-Type": "application/json",
        };

        try {
          const ewiResult = await fetchWithBQ({
            url: `bank/${_orgClientId()}/ewi/${_idrssd()}?quarter=${quarter}`,
            method: "GET",
            headers,
          });

          if (ewiResult.error) {
            return buildCustomErrorResponse(
              ewiResult.error,
              ewiResult.error.status,
            );
          }

          _queryApi.dispatch(
            setEwiNotificationStatus(
              ewiResult.data?.data?.ewi_notification_status,
            ),
          );

          return {
            data: ewiResult.data?.data,
          };
        } catch (error) {
          return buildCustomErrorResponse(error);
        }
      },

      providesTags: ["EWIDataForCurrentQuarter"],
    }),
    updateEWINotificationStatus: builder.query({
      async queryFn(_arg, api, _extraOptions, fetchWithBQ) {
        const headers = {
          Authorization: _authToken(),
          "Content-Type": "application/json",
        };

        try {
          const updatedEWINotificationStatus = await fetchWithBQ({
            url: `bank/${_orgClientId()}/ewi/${_idrssd()}?status=blank`,
            method: "PUT",
            headers,
          });

          if (updatedEWINotificationStatus.error) {
            return buildCustomErrorResponse(
              updatedEWINotificationStatus.error,
              updatedEWINotificationStatus.error.status,
            );
          }

          api.dispatch(setEwiNotificationStatus("blank"));

          return {
            data: updatedEWINotificationStatus.data?.data,
          };
        } catch (error) {
          return buildCustomErrorResponse(error);
        }
      },

      providesTags: ["UpdateEWINotification"],
    }),
  }),
});

export const {
  useGetDownloadReportQuarterListQuery,
  useGetFHSDataBasedOnQuarterQuery,
  useGetEWIDataQuery,
  useUpdateEWINotificationStatusQuery,
} = downloadFHSReportRTK;
